import { ChevronLeft, ChevronRight } from 'lucide-react';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** 'YYYY-MM' for the calendar month containing `date`. */
export function periodFromDate(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

/** 'April 2026' for a 'YYYY-MM' period string. */
export function periodLabel(period: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(period);
  if (!m) return period;
  const year  = Number(m[1]);
  const month = Number(m[2]);
  return `${MONTH_NAMES[month - 1]} ${year}`;
}

function shiftPeriod(period: string, delta: -1 | 1): string {
  const m = /^(\d{4})-(\d{2})$/.exec(period);
  if (!m) return period;
  const year  = Number(m[1]);
  const month = Number(m[2]);
  const date = new Date(Date.UTC(year, month - 1 + delta, 1));
  return periodFromDate(new Date(date.getUTCFullYear(), date.getUTCMonth(), 1));
}

interface Props {
  period:     string;          // 'YYYY-MM'
  onChange:   (period: string) => void;
  /** Disable the next-month arrow when we're at the current calendar month. */
  maxPeriod?: string;
}

export default function ClosePeriodSelector({ period, onChange, maxPeriod }: Props) {
  const today    = periodFromDate(new Date());
  const ceiling  = maxPeriod ?? today;
  const atMax    = period >= ceiling;

  return (
    <div className="inline-flex items-center gap-1 rounded-lg border border-stone-300 dark:border-stone-600 bg-white dark:bg-[#1e2521]">
      <button
        type="button"
        onClick={() => onChange(shiftPeriod(period, -1))}
        className="p-1.5 text-stone-500 hover:text-stone-700 dark:hover:text-stone-300 transition"
        title="Previous month"
      >
        <ChevronLeft size={16} />
      </button>
      <span className="px-2 text-sm font-medium text-stone-700 dark:text-stone-300 min-w-[7rem] text-center">
        {periodLabel(period)}
      </span>
      <button
        type="button"
        onClick={() => onChange(shiftPeriod(period, 1))}
        disabled={atMax}
        className="p-1.5 text-stone-500 hover:text-stone-700 dark:hover:text-stone-300 transition disabled:opacity-30"
        title={atMax ? 'Already at the current month' : 'Next month'}
      >
        <ChevronRight size={16} />
      </button>
    </div>
  );
}
